import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { CustomFurnitureItem, FurnitureCategory, Vec2 } from "./types";

const STORAGE_KEY = "room-canvas:custom-furniture:v1";
const STORAGE_VERSION = 1;

function uid(prefix = "cf"): string {
  return `${prefix}_${Math.random().toString(36).slice(2, 9)}`;
}

function trimName(input: string | undefined, fallback: string): string {
  const t = (input ?? "").trim();
  return t.length > 0 ? t : fallback;
}

/** Fields the creator supplies when saving a freshly-authored piece. */
export interface NewCustomFurniture {
  name: string;
  category: FurnitureCategory;
  width: number;
  depth: number;
  height: number;
  color: string;
  shape: Vec2[];
  /** Optional parts model (see `CustomFurnitureItem.model`). */
  model?: CustomFurnitureItem["model"];
  material?: CustomFurnitureItem["material"];
  frontKind?: CustomFurnitureItem["frontKind"];
}

/**
 * Where the personal custom-furniture catalog is currently being persisted.
 * Mirrors {@link import("./products").ProductsCloudStatus}; duplicated so the
 * two catalogs can drift independently without coupling.
 */
export type CustomFurnitureCloudStatus =
  | "local-only"
  | "syncing"
  | "synced"
  | "error";

interface CustomFurnitureState {
  /** All custom-furniture items, newest-first by `createdAt`. */
  items: CustomFurnitureItem[];

  /** Current cloud-sync status. Driven by the customFurnitureSync orchestrator. */
  cloudStatus: CustomFurnitureCloudStatus;

  /** Human-readable error from the most recent failed cloud op, or null. */
  cloudError: string | null;

  /**
   * Add a freshly-authored piece to the catalog. Returns the new
   * `CustomFurnitureItem.id` so the caller can place it on the canvas in the
   * same gesture.
   */
  addItem: (item: NewCustomFurniture) => string;

  /** Replace an existing item wholesale. Bumps `updatedAt`. No-ops if the id
   *  is unknown. `id`/`createdAt` are immutable and ignored in the patch. */
  updateItem: (id: string, patch: Partial<NewCustomFurniture>) => void;

  /** Rename an item. */
  renameItem: (id: string, name: string) => void;

  /** Delete a single item. */
  removeItem: (id: string) => void;

  /** Wipe the entire catalog. */
  clearAll: () => void;

  /**
   * Replace the entire items list. Used by the cloud-sync orchestrator when
   * reconciling a remote snapshot into local state. Kept separate from the
   * mutators so the orchestrator can suppress the echo write-back.
   */
  _setItemsFromSync: (items: CustomFurnitureItem[]) => void;

  /** Cloud-sync orchestrator hook — update the surface status. */
  _setCloudStatus: (
    status: CustomFurnitureCloudStatus,
    error?: string | null
  ) => void;
}

export const useCustomFurniture = create<CustomFurnitureState>()(
  persist(
    (set) => ({
      items: [],
      cloudStatus: "local-only",
      cloudError: null,

      addItem: (item) => {
        const id = uid();
        const now = Date.now();
        const created: CustomFurnitureItem = {
          id,
          name: trimName(item.name, "Custom piece"),
          category: item.category,
          width: item.width,
          depth: item.depth,
          height: item.height,
          color: item.color,
          shape: item.shape.map((p) => ({ x: p.x, y: p.y })),
          ...(item.model ? { model: structuredClone(item.model) } : {}),
          ...(item.material ? { material: { ...item.material } } : {}),
          ...(item.frontKind ? { frontKind: item.frontKind } : {}),
          createdAt: now,
          updatedAt: now,
        };
        set((s) => ({ items: [created, ...s.items] }));
        return id;
      },

      updateItem: (id, patch) =>
        set((s) => {
          const idx = s.items.findIndex((i) => i.id === id);
          if (idx === -1) return s;
          const existing = s.items[idx];
          const merged: CustomFurnitureItem = {
            ...existing,
            ...patch,
            // Preserve identity + creation time; normalize the name if patched.
            id: existing.id,
            createdAt: existing.createdAt,
            name:
              patch.name !== undefined
                ? trimName(patch.name, existing.name)
                : existing.name,
            shape: patch.shape
              ? patch.shape.map((p) => ({ x: p.x, y: p.y }))
              : existing.shape,
            updatedAt: Date.now(),
          };
          const without = s.items.filter((i) => i.id !== id);
          return { items: [merged, ...without] };
        }),

      renameItem: (id, name) =>
        set((s) => ({
          items: s.items.map((i) =>
            i.id === id
              ? { ...i, name: trimName(name, i.name), updatedAt: Date.now() }
              : i
          ),
        })),

      removeItem: (id) =>
        set((s) => ({ items: s.items.filter((i) => i.id !== id) })),

      clearAll: () => set({ items: [] }),

      _setItemsFromSync: (items) =>
        set({
          items: items.slice().sort((a, b) => b.createdAt - a.createdAt),
        }),

      _setCloudStatus: (status, error = null) =>
        set({ cloudStatus: status, cloudError: error }),
    }),
    {
      name: STORAGE_KEY,
      version: STORAGE_VERSION,
      // Persist only `items`. Cloud status is recomputed on every page load
      // (it depends on live auth state) so caching a stale value would lie.
      partialize: (s) => ({ items: s.items }),
    }
  )
);
